"use client";

// Company History — Wayflyer redesign (Figma 5152-29719 desktop / 5648-20395
// mobile). A centered "spotlight" timeline: one elevated card sits in the
// middle, its neighbours peeking dimmed on either side. Behaviour (per the
// design comments):
//   • horizontal slider, one card centred at a time
//   • drag left/right to bring a year into the centre
//   • the circular tick-dial above rotates and follows the scroll
//   • the centre card holds a loading bar that auto-fills; every ~4.5s it
//     reaches 100% and advances to the next year
//   • it loops, so autoplay never dead-ends

import useEmblaCarousel from "embla-carousel-react";
import * as React from "react";

import { GrainOverlay } from "@/components/ui/grain-overlay";
import { Section } from "@/components/ui/section";
import { SegmentedProgressBar } from "@/components/ui/segmented-progress-bar";
import { cn } from "@/lib/utils";

export interface HistoryEntry {
  year: string;
  title: string;
  description: string;
}

interface CompanyHistoryProps {
  title: string;
  entries: HistoryEntry[];
  /** Index the timeline opens centred on (the Figma "featured" milestone). */
  initialIndex?: number;
  className?: string;
}

// Exact "gradient/secondary" from Figma 5152-29719 (coral → magenta → blue).
const YEAR_TAG_GRADIENT =
  "linear-gradient(-75.8666deg, rgb(254, 99, 77) 3.2219%, rgb(251, 81, 112) 11.287%, rgb(241, 69, 146) 19.352%, rgb(224, 64, 180) 27.416%, rgb(206, 60, 201) 35.481%, rgb(191, 54, 213) 43.546%, rgb(175, 50, 224) 51.611%, rgb(157, 45, 234) 59.676%, rgb(138, 40, 243) 67.741%, rgb(118, 35, 249) 75.805%, rgb(95, 31, 254) 83.87%, rgb(65, 29, 255) 91.935%, rgb(0, 30, 255) 100%)";

// How long each card holds the spotlight before autoplay advances.
const AUTOPLAY_MS = 4500;
// Ticks in the centre card's loading bar — thin lines spread across the full
// width, matching Figma.
const LOADER_TICKS = 44;

export function CompanyHistory({
  title,
  entries,
  initialIndex = 0,
  className,
}: CompanyHistoryProps) {
  const startIndex = clampIndex(initialIndex, entries.length);

  const sectionRef = React.useRef<HTMLElement>(null);
  const inViewRef = React.useRef(false);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    loop: true,
    skipSnaps: false,
    containScroll: false,
    startIndex,
  });

  const [selectedIndex, setSelectedIndex] = React.useState(startIndex);
  const [progress, setProgress] = React.useState(0);
  // Lock carousel height to the tallest card (incl. loader) so the section
  // doesn't resize as the spotlight moves — WEB-718.
  const [maxCardHeight, setMaxCardHeight] = React.useState<number | null>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  // Target angle for the dial. The wheel turns one step each time the card
  // changes, then rests; TimelineDial animates toward this target and morphs
  // each tick as it passes the fixed centre highlight.
  const [targetRotation, setTargetRotation] = React.useState(0);

  const reducedMotion = usePrefersReducedMotion();

  const scrollToIndex = React.useCallback(
    (index: number) => {
      if (!emblaApi || index === selectedIndex) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi, selectedIndex]
  );

  // Holds autoplay only while the user is actively dragging.
  const draggingRef = React.useRef(false);
  // Accumulated index that never wraps — drives a continuous dial rotation
  // across the loop boundary instead of snapping back at 9 → 0.
  const virtualIndexRef = React.useRef(startIndex);

  React.useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        inViewRef.current = entry?.isIntersecting ?? false;
      },
      { threshold: 0.35 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  React.useLayoutEffect(() => {
    const measureEl = measureRef.current;
    if (!measureEl) return;

    const measure = () => {
      const cards = measureEl.querySelectorAll<HTMLElement>(
        "[data-history-card]"
      );
      if (cards.length === 0) return;
      const tallest = Math.max(
        ...Array.from(cards, (card) => card.getBoundingClientRect().height)
      );
      setMaxCardHeight((prev) => (prev === tallest ? prev : tallest));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(measureEl);
    return () => ro.disconnect();
  }, [entries]);

  // --- Keep selected index + dial rotation in sync with the carousel ---
  React.useEffect(() => {
    if (!emblaApi) return;
    const len = entries.length;
    // One full loop of the timeline = one full turn of the wheel, so each card
    // advances the wheel by exactly the gap between two "big" ticks.
    const step = 360 / len;

    const onSelect = () => {
      const next = emblaApi.selectedScrollSnap();
      setSelectedIndex(next);

      // Shortest-path delta so the dial turns the way the cards travelled
      // (forward on the autoplay 9 → 0 wrap, backward when dragged back).
      const current = ((virtualIndexRef.current % len) + len) % len;
      let delta = next - current;
      if (delta > len / 2) delta -= len;
      if (delta < -len / 2) delta += len;
      virtualIndexRef.current += delta;
      // Offset by startIndex so the wheel sits centred (rotation 0) on the
      // featured card, then turns one step each time the card changes.
      // Positive rotation sweeps the ticks right → left through the centre
      // highlight, travelling with the cards rather than against them.
      setTargetRotation((virtualIndexRef.current - startIndex) * step);
    };

    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, entries.length, startIndex]);

  // --- Autoplay loop + loading-bar progress ---
  // Driven by a wall-clock interval (not requestAnimationFrame) so it keeps
  // ticking reliably — rAF is fully paused while the tab is backgrounded, which
  // is what made the carousel occasionally "stop" and not restart.
  React.useEffect(() => {
    if (!emblaApi || reducedMotion) return;

    let last = performance.now();
    let elapsed = 0;

    // Reset the timer whenever the centred card changes (drag or autoplay).
    const onSelectReset = () => {
      elapsed = 0;
      setProgress(0);
    };
    // Only hold the timer while the user is actively dragging — never on hover,
    // so the carousel keeps playing on its own. `settle` always fires after a
    // drag, so the flag self-heals even if a pointerUp is missed.
    const onPointerDown = () => {
      draggingRef.current = true;
    };
    const onResume = () => {
      draggingRef.current = false;
    };
    emblaApi.on("select", onSelectReset);
    emblaApi.on("pointerDown", onPointerDown);
    emblaApi.on("pointerUp", onResume);
    emblaApi.on("settle", onResume);

    const id = window.setInterval(() => {
      const now = performance.now();
      // Cap dt so a throttled tab doesn't jump several cards forward at once.
      const dt = Math.min(now - last, 200);
      last = now;
      // Don't advance while dragging, off-screen, or while the tab is hidden.
      if (draggingRef.current || document.hidden || !inViewRef.current) return;
      elapsed += dt;
      if (elapsed >= AUTOPLAY_MS) {
        elapsed = 0;
        emblaApi.scrollNext();
      }
      setProgress(Math.min(elapsed / AUTOPLAY_MS, 1));
    }, 50);

    return () => {
      window.clearInterval(id);
      emblaApi.off("select", onSelectReset);
      emblaApi.off("pointerDown", onPointerDown);
      emblaApi.off("pointerUp", onResume);
      emblaApi.off("settle", onResume);
    };
  }, [emblaApi, reducedMotion]);

  return (
    <Section
      ref={sectionRef}
      data-testid="company-history-section"
      className={cn(
        "bg-grey-950 text-grey-0 relative isolate overflow-hidden",
        "px-0 py-0 lg:px-0",
        className
      )}
    >
      <GrainOverlay
        baseFrequency={0.85}
        numOctaves={2}
        contrast={2.6}
        opacity={0.45}
        blendMode="soft-light"
      />
      {/* Purple glow rising from the bottom, matching the Figma backdrop */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-[55%]"
        style={{
          background:
            "radial-gradient(70% 100% at 50% 100%, oklch(0.45 0.27 295 / 0.65) 0%, transparent 70%)",
        }}
      />
      {/* Faint dotted grid */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.18]"
        style={{
          backgroundImage:
            "radial-gradient(oklch(0.7 0.02 280 / 0.5) 0.5px, transparent 0.5px)",
          backgroundSize: "24px 24px",
        }}
      />

      {/* Header — the large rotating wheel fills this area up to the top, with
          the title overlaid in the upper third (matching the Figma). */}
      <div className="relative">
        {/* Big tick wheel spanning the section up to the top */}
        <TimelineDial
          targetRotation={targetRotation}
          reducedMotion={reducedMotion}
          cards={entries.length}
        />
        <h2 className="text-ds-heading-h4 md:text-ds-heading-h3 text-grey-0 pointer-events-none absolute inset-x-0 top-16 z-10 text-center md:top-[24%]">
          {title}
        </h2>
      </div>

      {/* Centered spotlight carousel — Figma 10378:43204 keeps ~56px between
          the dial ticks and the active card top at desktop (359 − 303). */}
      <div className="relative pt-8 pb-16 md:pt-12 md:pb-20 lg:pt-14 lg:pb-24">
        {/* Off-screen sizer: every card rendered as active so max height is
            stable across breakpoints and copy lengths (WEB-718). */}
        <div
          ref={measureRef}
          aria-hidden
          className="pointer-events-none invisible absolute -z-20 flex w-full items-start"
        >
          {entries.map((entry) => (
            <div
              key={`measure-${entry.year}-${entry.title}`}
              className="min-w-0 shrink-0 basis-[280px] px-2 md:basis-[300px]"
            >
              <HistoryCard entry={entry} isActive measureOnly />
            </div>
          ))}
        </div>
        <div className="overflow-hidden" ref={emblaRef}>
          <div
            className="flex items-start"
            style={maxCardHeight ? { minHeight: maxCardHeight } : undefined}
          >
            {entries.map((entry, idx) => {
              const isActive = idx === selectedIndex;
              return (
                <div
                  key={`${entry.year}-${entry.title}`}
                  className="flex min-w-0 shrink-0 basis-[280px] px-2 md:basis-[300px]"
                  style={
                    maxCardHeight ? { minHeight: maxCardHeight } : undefined
                  }
                >
                  <HistoryCard
                    entry={entry}
                    isActive={isActive}
                    progress={isActive ? progress : 0}
                    onNavigate={isActive ? undefined : () => scrollToIndex(idx)}
                    className={maxCardHeight ? "my-auto w-full" : undefined}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Section>
  );
}

function HistoryCard({
  entry,
  isActive,
  progress = 0,
  onNavigate,
  measureOnly = false,
  className,
}: {
  entry: HistoryEntry;
  isActive: boolean;
  progress?: number;
  onNavigate?: () => void;
  /** Render for height measurement only — always uses active layout. */
  measureOnly?: boolean;
  className?: string;
}) {
  const showActive = measureOnly || isActive;

  return (
    <article
      data-history-card
      aria-current={!measureOnly && isActive ? "true" : undefined}
      aria-hidden={measureOnly ? true : undefined}
      className={cn(
        "rounded-ds-xl relative flex flex-col gap-4 p-6 transition-[background-color,color,opacity] duration-500 ease-out",
        showActive ? "bg-grey-950" : "bg-transparent",
        onNavigate && "cursor-pointer",
        className
      )}
    >
      {onNavigate ? (
        <button
          type="button"
          aria-label={`View ${entry.year}: ${entry.title}`}
          onClick={onNavigate}
          className="focus-visible:ring-pulse-700 rounded-ds-xl absolute inset-0 z-10 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
        />
      ) : null}
      <YearTag year={entry.year} isActive={showActive} />
      <h3
        className={cn(
          "text-ds-heading-h5 text-balance transition-colors duration-500",
          showActive ? "text-grey-0" : "text-grey-600"
        )}
      >
        {entry.title}
      </h3>
      <p
        className={cn(
          "text-ds-body-sm transition-colors duration-500",
          showActive ? "text-grey-400" : "text-grey-700"
        )}
      >
        {entry.description}
      </p>
      {/* Loader slot is always in layout so card height stays constant (WEB-718). */}
      <div
        className={cn(
          "transition-opacity duration-500",
          showActive ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        aria-hidden={!showActive || measureOnly}
      >
        <Loader progress={measureOnly ? 0 : progress} />
      </div>
    </article>
  );
}

function YearTag({ year, isActive }: { year: string; isActive: boolean }) {
  return (
    <span
      className={cn(
        "text-ds-label-sm rounded-ds-s inline-flex w-fit items-center px-2 py-0.5 font-medium transition-colors duration-500",
        isActive ? "text-grey-0" : "border-grey-700 text-grey-500 border"
      )}
      style={isActive ? { backgroundImage: YEAR_TAG_GRADIENT } : undefined}
    >
      {year}
    </span>
  );
}

function Loader({ progress }: { progress: number }) {
  return (
    <SegmentedProgressBar
      progress={progress}
      tickCount={LOADER_TICKS}
      className="rounded-ds-m mt-2 [&_span]:transition-colors [&_span]:duration-150"
    />
  );
}

function clampIndex(index: number, length: number) {
  if (length <= 0) return 0;
  return Math.min(Math.max(index, 0), length - 1);
}

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
};

// A large tick wheel matching Figma 5152-29719 (Group 92). The highlight is
// anchored to the SCREEN centre (bottom-centre of the arc, 90°): the centred
// line is tall + white, with length and colour falling off toward the edges.
// Ticks pass THROUGH this fixed highlight as the wheel spins one step per card,
// so each line morphs — growing + whitening as it slides into centre, then
// shrinking + greying as it leaves. The spin is animated in JS (rAF) so the
// morph tracks the live in-flight angle every frame; a CSS transition on the
// group can't do this, as each line's size would jump to its final state
// immediately while the group rotated.
const DIAL_NORMAL_LEN = 16; // length unit (pre-SCALE) for a normal tick
const DIAL_BIG_LEN = 37; // the centred line
const DIAL_COLOR_BAND = 52; // deg — how far the white glow reaches from centre
const DIAL_SPIN_MS = 700; // per-card spin duration
const DIAL_WHITE: readonly [number, number, number] = [255, 255, 255];
const DIAL_EDGE: readonly [number, number, number] = [120, 128, 150];

const clamp01 = (n: number) => Math.min(Math.max(n, 0), 1);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
const smoothstep = (t: number) => t * t * (3 - 2 * t);

function TimelineDial({
  targetRotation,
  reducedMotion,
  cards,
}: {
  targetRotation: number;
  reducedMotion: boolean;
  cards: number;
}) {
  // 6 ticks per card so a tick always rests exactly at centre; the wheel turns
  // 360/cards per card (a whole multiple of the tick spacing).
  const TICKS = cards * 6;
  const SPACING = 360 / TICKS; // deg between adjacent ticks
  const VB_W = 100;
  const VB_H = 38;
  const CX = 50;
  const CY = -13.9; // circle centre sits above the viewBox top
  const R = 51.9;
  const SCALE = 0.156; // length unit → viewBox units (≈37px big at capped width)

  // Animate the rotation in JS so the per-tick morph can track the live,
  // in-flight angle each frame — not just the resting target.
  const [liveRotation, setLiveRotation] = React.useState(targetRotation);
  const liveRef = React.useRef(targetRotation);
  const fromRef = React.useRef(targetRotation);
  const startRef = React.useRef(0);
  const rafRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (reducedMotion) {
      liveRef.current = targetRotation;
      setLiveRotation(targetRotation);
      return;
    }
    // Tween from wherever the wheel currently is, so re-targeting mid-spin
    // (fast drags, autoplay) chains smoothly instead of snapping.
    fromRef.current = liveRef.current;
    startRef.current = performance.now();

    const tick = () => {
      const t = clamp01((performance.now() - startRef.current) / DIAL_SPIN_MS);
      const value = lerp(fromRef.current, targetRotation, easeOutCubic(t));
      liveRef.current = value;
      setLiveRotation(value);
      rafRef.current = t < 1 ? requestAnimationFrame(tick) : null;
    };

    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [targetRotation, reducedMotion]);

  return (
    <div
      aria-hidden
      className="pointer-events-none relative z-0 mx-auto w-full max-w-[640px]"
    >
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMax meet"
        className="block w-full"
      >
        {/* The group rotates to the live angle; each tick's length + colour are
            computed from that same angle, so geometry and morph stay in sync. */}
        <g
          style={{
            transform: `rotate(${liveRotation}deg)`,
            transformBox: "view-box",
            transformOrigin: `${CX}px ${CY}px`,
          }}
        >
          {Array.from({ length: TICKS }).map((_, i) => {
            const baseAngle = i * SPACING;
            const rad = (baseAngle * Math.PI) / 180;
            // Live screen angle of this tick, as a distance (0–180°) from the
            // bottom-centre highlight at 90°.
            const screen =
              (((baseAngle + liveRotation - 90) % 360) + 360) % 360;
            const d = screen > 180 ? 360 - screen : screen;
            // Height: a smooth cosine bump that reaches BIG only at the very
            // centre and is back to NORMAL one tick away — so the wheel shows
            // exactly two sizes at rest, yet grows/shrinks smoothly in motion.
            const bump =
              d >= SPACING ? 0 : Math.cos((d / SPACING) * (Math.PI / 2));
            const len =
              (DIAL_NORMAL_LEN + (DIAL_BIG_LEN - DIAL_NORMAL_LEN) * bump) *
              SCALE;
            // Colour: white at centre, easing to a faint blue-grey across a
            // wider band; far ticks also fade out toward the corners.
            const cMix = smoothstep(clamp01(d / DIAL_COLOR_BAND));
            const cr = Math.round(lerp(DIAL_WHITE[0], DIAL_EDGE[0], cMix));
            const cg = Math.round(lerp(DIAL_WHITE[1], DIAL_EDGE[1], cMix));
            const cb = Math.round(lerp(DIAL_WHITE[2], DIAL_EDGE[2], cMix));
            const opacity = lerp(1, 0.18, clamp01(d / 95));
            const inner = R - len;
            return (
              <line
                key={i}
                x1={CX + inner * Math.cos(rad)}
                y1={CY + inner * Math.sin(rad)}
                x2={CX + R * Math.cos(rad)}
                y2={CY + R * Math.sin(rad)}
                stroke={`rgb(${cr}, ${cg}, ${cb})`}
                strokeOpacity={opacity}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
              />
            );
          })}
        </g>
      </svg>
    </div>
  );
}
